import React from 'react';
import { type Address } from 'viem';
import StatusRow from './StatusRow';
import ActionButton from './ActionButton';
import VerificationModal from './VerificationModal';
import {
  AirdropStatus,
  VerificationStatus,
  type LoadingState,
  type UIStatus,
} from '../types/contracts';

interface AirdropCardProps {
  // Wallet state
  address: Address | undefined;
  isConnected: boolean;
  isWalletClientLoading: boolean;

  // Airdrop state
  status: AirdropStatus;
  verificationStatus: VerificationStatus;
  isClaimed: boolean;
  allocation: string;
  humanStatusChecked: boolean;
  palmVerified: boolean;
  palmStatusLoading: boolean;
  userExists: boolean | null;
  loadingState: LoadingState;
  error: string | null;

  // Token information
  tokenBalance: string;
  tokenSymbol: string;

  // Verification modal state
  showVerificationModal: boolean;
  verificationLoading: boolean;
  verificationError: string | null;
  verificationSuccess: boolean;

  // Action methods
  onClaimAirdrop: () => void;
  onRetry: () => void;
  onOpenVerificationModal: () => void;
  onCloseVerificationModal: () => void;
  onPerformVerification: () => void;
}

/**
 * AirdropCard - Core airdrop card component
 * 
 * Contains:
 * - Status rows (wallet, human proof)
 * - Error notification
 * - Main action button
 * - Detail statistics (total amount, personal allocation)
 * - Social links
 */
const AirdropCard: React.FC<AirdropCardProps> = ({
  address,
  isConnected,
  isWalletClientLoading,
  status,
  verificationStatus,
  isClaimed,
  allocation,
  tokenBalance,
  tokenSymbol,
  humanStatusChecked,
  palmVerified,
  palmStatusLoading,
  userExists,
  loadingState,
  error,
  showVerificationModal,
  verificationLoading,
  verificationError,
  verificationSuccess,
  onClaimAirdrop,
  onRetry,
  onOpenVerificationModal,
  onCloseVerificationModal,
  onPerformVerification,
}) => {
  // Get status display helpers
  const getWalletStatus = (): UIStatus => {
    if (status === AirdropStatus.DISCONNECTED) return 'pending';
    if (status === AirdropStatus.CONNECTING) return 'loading';
    return 'success';
  };

  /**
   * Get human verification status and display text
   * Based on palmVerified field from backend API
   */
  const getHumanStatusInfo = (): { status: UIStatus; text: string; showVerifyButton: boolean } => {
    // If user not registered, show not registered status
    if (userExists === false) {
      return { status: 'pending', text: 'Not Registered', showVerifyButton: false };
    }

    // If loading palm status, show loading
    if (palmStatusLoading) {
      return { status: 'loading', text: 'Checking...', showVerifyButton: false };
    }

    // If status not checked yet, show pending
    if (!humanStatusChecked) {
      return { status: 'pending', text: 'Checking', showVerifyButton: false };
    }

    // Based on palmVerified field
    if (palmVerified) {
      return { status: 'success', text: 'Verified', showVerifyButton: false };
    }

    // Not verified - show Verify button
    return { status: 'pending', text: 'Not Verified', showVerifyButton: true };
  };

  const humanStatusInfo = getHumanStatusInfo();

  return (
    <div className="w-full max-w-lg z-10 flex flex-col gap-4 md:gap-5 items-center justify-center py-4 md:py-6">
      {/* Header text */}
      <div className="text-center space-y-2 reveal delay-1">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight">
          Proof of <span className="text-green-500">Humanity</span>
        </h1>
        <p className="text-white/40 text-sm md:text-base max-w-xs mx-auto font-medium leading-relaxed">
          Sandbox Environment (on Testnet)
        </p>
      </div>

      {/* Verification guidance banner */}
      <div className="w-full reveal delay-2 px-4">
        <div className="p-3 rounded-xl bg-blue-950/50 backdrop-blur-sm border border-blue-500/20 text-center space-y-2">
          {/* Testnet environment notice */}
          <div className="flex items-center justify-center gap-1.5">
            <span className="text-yellow-400 text-xs">⚠️</span>
            <p className="text-[10px] text-yellow-300 font-semibold uppercase tracking-wide">
              Testnet Sandbox Environment
            </p>
          </div>

          {/* Verification instruction */}
          <p className="text-xs text-blue-300 leading-relaxed">
            <span className="font-semibold">Important:</span> Please verify your humanity first at{' '}
            <a
              href="https://app.sandbox.humanity.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline transition-colors font-medium"
            >
              app.sandbox.humanity.org
            </a>
          </p>

          {/* Additional notice */}
          <p className="text-[10px] text-blue-400/60 leading-relaxed">
            All data, tokens, and transactions are for <span className="text-blue-300 font-medium">testing purposes only</span>
          </p>
        </div>
      </div>

      {/* Core interaction card */}
      <div className="animate-float reveal delay-2 w-full">
        <div className="glass rounded-[32px] p-0.5 shadow-[0_0_60px_rgba(0,0,0,0.4)] group">
          <div className="bg-[#080808] rounded-[30px] p-5 md:p-6 relative overflow-hidden">
            <div className="scanline" />

            <div className="space-y-4 relative">
              <div className="space-y-2">
                <div className="flex justify-between items-end px-1">
                  <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">
                    Airdrop Check
                  </h2>
                </div>

                <div className="space-y-1.5">
                  <StatusRow
                    label="EVM Wallet"
                    value={address ? `${address.slice(0, 6)}...${address.slice(-4)}` : undefined}
                    status={getWalletStatus()}
                  />
                  <StatusRow
                    label="Human Proof"
                    status={humanStatusInfo.status}
                    statusText={humanStatusInfo.text}
                    showVerifyButton={humanStatusInfo.showVerifyButton}
                    onVerify={onOpenVerificationModal}
                  />
                </div>
              </div>

              {/* Error notification */}
              {error && (
                <div className="p-2.5 rounded-xl bg-red-950/90 backdrop-blur-sm border border-red-500/30 text-red-300 text-xs leading-relaxed">
                  {verificationStatus === VerificationStatus.Failed ? (
                    <span>
                      Human verification failed. Please verify your humanity at{' '}
                      <a
                        href="https://app.sandbox.humanity.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-400 hover:text-red-300 underline font-medium transition-colors"
                      >
                        app.sandbox.humanity.org
                      </a>
                      {' '}and try again.
                    </span>
                  ) : (
                    error
                  )}
                </div>
              )}

              <div className="pt-0 flex">
                <ActionButton
                  isConnected={isConnected}
                  status={status}
                  isClaimed={isClaimed}
                  loadingState={loadingState}
                  isWalletClientLoading={isWalletClientLoading}
                  palmVerified={palmVerified}
                  userExists={userExists}
                  onClaimAirdrop={onClaimAirdrop}
                  onRetry={onRetry}
                  onOpenVerificationModal={onOpenVerificationModal}
                />
              </div>

              {/* Bottom details */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] text-center">
                  <div className="text-[9px] text-white/20 uppercase font-bold tracking-widest mb-0.5">
                    Your Balance
                  </div>
                  <div className="text-sm font-bold">
                    {tokenBalance} <span className="text-green-500 text-[10px]">${tokenSymbol}</span>
                  </div>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] text-center">
                  <div className="text-[9px] text-white/20 uppercase font-bold tracking-widest mb-0.5">
                    Your Share
                  </div>
                  <div className="text-sm font-bold">
                    {allocation || '0'} <span className="text-green-500 text-[10px]">$DEMO</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      <VerificationModal
        isOpen={showVerificationModal}
        isLoading={verificationLoading}
        error={verificationError}
        success={verificationSuccess}
        onClose={onCloseVerificationModal}
        onVerify={onPerformVerification}
      />
    </div>
  );
};

export default AirdropCard;
